import { Box, Text, VStack, HStack, Badge } from "@chakra-ui/react";

// Components
import CustomModal from "../../../../shared/Modals/CustomModal";

// Hooks
import { useReservations } from "../../../../hooks/useReservations";

interface DeleteReservationModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	reservation: {
		id: number;
		analystName: string;
		passGroupId: string;
		isAllPassGroups: boolean;
		benefitCode: string;
		isAllBenefits: boolean;
	} | null;
}

/**
 * Confirmation modal for removing (soft deleting) a claim reservation
 */
export default function DeleteReservationModal({
	isOpen,
	onClose,
	onSuccess,
	reservation
}: DeleteReservationModalProps) {
	const { removeReservation, isLoading } = useReservations();

	const handleConfirm = async () => {
		if (!reservation) return;

		const response = await removeReservation(reservation.id);

		if (response) {
			onSuccess();
			onClose();
		}
	};

	if (!reservation) return null;

	return (
		<CustomModal
			isOpen={isOpen}
			isCentered={true}
			closeOnOverlayClick={false}
			onCancel={onClose}
			onContinue={handleConfirm}
			size="md"
			title="Remove Claim Reservation?"
			continueText="Remove"
			continueColorScheme="red"
			continueLoading={isLoading}
			content={
				<Box py={4}>
					<Text mb={4}>Are you sure you want to remove this auto-assignment rule?</Text>

					<VStack spacing={3} align="stretch" bg="gray.50" p={4} borderRadius="md">
						<HStack>
							<Text fontWeight="semibold" minW="120px">
								Analyst:
							</Text>
							<Text>{reservation.analystName}</Text>
						</HStack>

						<HStack>
							<Text fontWeight="semibold" minW="120px">
								Pass Group:
							</Text>
							{reservation.isAllPassGroups ? (
								<Badge colorScheme="blue" fontSize="xs">
									All Groups
								</Badge>
							) : (
								<Text>{reservation.passGroupId}</Text>
							)}
						</HStack>

						<HStack>
							<Text fontWeight="semibold" minW="120px">
								Benefit Code:
							</Text>
							{reservation.isAllBenefits ? (
								<Badge colorScheme="blue" fontSize="xs">
									All Benefits
								</Badge>
							) : (
								<Text>{reservation.benefitCode}</Text>
							)}
						</HStack>
					</VStack>

					<Text mt={4} fontSize="sm" color="gray.600">
						This reservation will be soft-deleted and can be recovered later if needed.
					</Text>
				</Box>
			}
		/>
	);
}
